import { useState } from "react";
import { CarouselGalleryUI } from "./CarouselGalleryUI";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { isAvailableToOrder } from "@/utils/product/isAvailableToOrder";
import {
  hasSocialProofMessages,
  videoPreloadValues,
  SocialProofMessagesProperties,
} from "@/components/Galleries/utils/utils.ts";
import { homeAccesoriesImageTestMapping } from "@/components/Galleries/CarouselGallery/experience/mapping.ts";
import get from "lodash/get";
import { isM2MRomanBlindOrCurtain } from "@/utils/product/isMadeToMeasure";
import { useRouter } from "next/router";
import { filterM2MImages } from "@/components/Galleries/utils/filterM2MImages";
import { parseFlagEnumValue } from "@/utils/flags/parseFlagEnumValue.ts";
import {
  standardComponent,
  useStandardProps,
} from "@/context/StandardPropsContext/StandardPropsContext";

export const CarouselGallery = standardComponent("CarouselGallery", () => {
  const {
    product,
    configProps,
    flags,
    experiencesRawObject,
    runtimeConfig,
    sizes,
  } = useStandardProps();
  const router = useRouter();
  const { variant } = useVariant(product);
  const media = "media" in product ? product.media : null;
  const videos = media?.videos || [];
  const [showSocialProofMessages, setShowSocialProofMessages] = useState(
    flags.showSocialProofMessages.value === true,
  );
  const [fadeOutSocialProofMessages, setFadeOutSocialProofMessages] =
    useState(false);
  const spm = hasSocialProofMessages(configProps)
    ? configProps.SocialProofMessages
    : undefined;
  const socialProofMessagesProperties: SocialProofMessagesProperties =
    product.__typename === "SimpleProduct" && spm
      ? {
          appId: spm.appId ?? "",
          withClose: spm.withClose || true,
          productId: product.aliases.pimProductId,
          brand: product.brand.name,
          availableToOrder: isAvailableToOrder(product),
          experiencesRawObject,
          showSocialProofMessages,
          setShowSocialProofMessages,
          fadeOutSocialProofMessages,
          setFadeOutSocialProofMessages,
          monetateEnvironment: runtimeConfig.monetateEnvironment,
        }
      : undefined;
  const condensedGallery =
    configProps?.condensedGallery === true ||
    flags.condensedGallery.value === true;

  const videoPreload = parseFlagEnumValue(
    String(flags.videoPreload?.value),
    videoPreloadValues,
    "auto",
  );

  const amendCarouselZoomIcon = flags.amendCarouselZoomIcon?.value;

  if (!variant) return null;

  let homeAccessoriesTestImages;

  if (flags.homeAccessoriesImageTest.value) {
    const replacementImages = get(homeAccesoriesImageTestMapping, [
      product.aliases.pimProductId,
      variant.displayCode,
    ]);

    if (replacementImages) {
      const [primary = "", ...alternatives] = replacementImages;
      homeAccessoriesTestImages = { primary, alternatives };
    }
  }

  let images = variant.images;
  if (isM2MRomanBlindOrCurtain(product)) {
    images = filterM2MImages(images, router.query);
  }

  return (
    <CarouselGalleryUI
      title={variant.title}
      images={homeAccessoriesTestImages || images}
      videos={videos}
      sizes={sizes}
      hasSocialProofMessagesExperience={true}
      hasImmediateStateUpdates={!!flags.hasImmediateStateUpdates.value}
      socialProofMessagesProperties={socialProofMessagesProperties}
      hasTransparentThumbnails={configProps?.hasTransparentThumbnails === true}
      hasDeferredMonetateQEvents={!!flags.hasDeferredMonetateQEvents.value}
      hasDeferredIntersectionObserverCallback={
        !!flags.hasDeferredIntersectionObserverCallback.value
      }
      hasSuperZoom={!flags.isSuperZoomDisabled.value}
      dataComponent="CarouselGallery"
      useFetchPriority={!!flags.usePrefetching.value}
      useResponsiveImages={!!flags.useResponsiveImages.value}
      carouselGalleryInpTest={flags.carouselGalleryInpTest.value === true}
      videoPreload={videoPreload}
      condensedGallery={condensedGallery}
      experiencesRawObject={experiencesRawObject}
      runtimeConfig={runtimeConfig}
      amendCarouselZoomIcon={amendCarouselZoomIcon}
    />
  );
});
